import { useCallback, useMemo } from "react";
import { Modal } from "shared/components/Modal";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";

import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  CreateLeadPayload,
  useFormController
} from "shared/app/LeadsTableView/components/CreateLeadModal/useFormController";
import { BasicInfoSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/BasicInfo";
import { ContactInfoSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/ContactInfo";
import { InterestedInSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/InterestedIn";
import { DetailsSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/Details";
import { Footer } from "shared/app/LeadsTableView/components/CreateLeadModal/Footer";
import { useDuplicateCheck } from "shared/app/LeadsTableView/components/CreateLeadModal/hooks/useDuplicateCheck";

export interface CreateLeadDefaults {
  property?: { id: number; name: string };
  unit?: { id: number; title: string };
  // 10-digit phone (no country code) to pre-populate the primary phone field.
  primaryPhone?: string;
  // When converting an AI call transcript, links the new lead to that transcript
  // server-side in the create request (avoids a separate, orphan-prone PATCH).
  callTranscriptId?: number;
}

export const CreateLeadModal = ({
  open,
  onClose,
  refetchLeads,
  defaults,
  onCreated,
  zIndex
}: {
  open: boolean;
  onClose: () => void;
  refetchLeads: () => void;
  defaults?: CreateLeadDefaults;
  // Fires with the created lead after a successful save. Optional so existing
  // callers are unaffected.
  onCreated?: (lead: { id: number }) => void;
  // Lets callers raise the modal above higher-stacking surfaces (e.g. a drawer).
  zIndex?: number;
}) => {
  const initialValues = useMemo<Partial<CreateLeadPayload> | undefined>(() => {
    if (!defaults?.property && !defaults?.unit && !defaults?.primaryPhone)
      return undefined;
    return {
      property_id: defaults.property?.id,
      unit_id: defaults.unit?.id,
      primary_phone: defaults.primaryPhone
    };
  }, [defaults]);

  const initialLabels = useMemo(
    () => ({
      property: defaults?.property?.name,
      unit: defaults?.unit?.title
    }),
    [defaults]
  );

  const form = useFormController({ initialValues });
  const {
    duplicates,
    check: checkDuplicates,
    reset: resetDuplicates
  } = useDuplicateCheck();

  const handleContactFieldBlur = useCallback(() => {
    const allEmails = [
      form.values.primary_email ?? "",
      ...(form.values.other_emails ?? [])
    ];
    const allPhones = [
      form.values.primary_phone ?? "",
      ...(form.values.other_phones ?? [])
    ];
    checkDuplicates(allEmails, allPhones);
  }, [
    form.values.primary_email,
    form.values.primary_phone,
    form.values.other_emails,
    form.values.other_phones,
    checkDuplicates
  ]);

  const { mutateAsync: createLead, isPending } = useMutation({
    mutationKey: ["CREATE_LEAD"],
    mutationFn: async (values: CreateLeadPayload) => {
      // Build contact methods; skip empty primaries, add "1" prefix to phones.
      const processedContactMethods = [
        ...(values.primary_phone?.trim()
          ? [
              {
                type: "phone",
                value: `1${values.primary_phone}`,
                is_primary: true
              }
            ]
          : []),
        ...(values.primary_email?.trim()
          ? [
              {
                type: "email",
                value: values.primary_email,
                is_primary: true
              }
            ]
          : []),
        ...values.other_phones
          .filter((phone) => phone.trim() !== "")
          .map((phone) => ({
            type: "phone",
            value: `1${phone}`,
            is_primary: false
          })),
        ...values.other_emails
          .filter((email) => email.trim() !== "")
          .map((email) => ({ type: "email", value: email, is_primary: false }))
      ];

      return instance
        .post<{ id: number }>("/portal/v1/leads", {
          ...values,
          contact_methods: processedContactMethods,
          call_transcript_id: defaults?.callTranscriptId,
          source_id: Number(values.source_id) || undefined,
          assignee_ids:
            values.partner_user_id && Number(values.partner_user_id)
              ? [Number(values.partner_user_id)]
              : undefined
        })
        .then((res) => res.data);
    }
  });

  const handleClose = () => {
    onClose();
    form.reset();
    resetDuplicates();
  };

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      zIndex={zIndex}
      panelClassName="w-full max-w-[816px] p-0 overflow-y-hidden mx-5"
    >
      <form
        onSubmit={form.onSubmit({
          onSuccess: () => {
            createLead(form.values as CreateLeadPayload).then((lead) => {
              refetchLeads();
              onCreated?.(lead);
              handleClose();
            });
          }
        })}
        className="flex flex-col max-h-[85vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-alpha-100 bg-white sticky top-0 z-10">
          <div>
            <h3 className="text-xl leading-6 font-bold text-content-1000">
              Create lead
            </h3>
          </div>
          <IconButton
            aria-label="Close"
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.SM}
            HugeIcon={Cancel01Icon}
            onClick={handleClose}
          />
        </div>
        {/* Body (scroll area) */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6 bg-background-900">
          <BasicInfoSection register={form.register} />
          <ContactInfoSection
            form={form}
            duplicates={duplicates}
            onContactFieldBlur={handleContactFieldBlur}
          />
          <InterestedInSection form={form} initialLabels={initialLabels} />
          <DetailsSection form={form} propertyId={defaults?.property?.id} />
        </div>
        <Footer onClose={handleClose} isPending={isPending} />
      </form>
    </Modal>
  );
};
